/* VoiceSearchContext — site-wide voice navigation (P1–P5).
 *
 * One listener mounted at app root. Two mic sessions:
 *   • Wake-word scan (always-on, if the user has opted in) looks only for
 *     "hey recipe", "hey recipe filer" or "hey filer". Match = open overlay.
 *   • Command session (opens when FAB tapped OR wake-word matched) listens
 *     for one command, routes it, then closes.
 *
 * Three resolution tiers (checked in order):
 *   T1 — Deterministic page routes (/dashboard, /pricing, …)
 *   T2 — Per-page registered commands via useVoiceCommand() hook (P5)
 *   T3 — Global actions (help, back, log out, stop listening)
 *   T4 — Fuzzy entity resolve via /api/voice/resolve (recipes/cookbooks)
 *
 * Kitchen View (/k/*) owns the mic exclusively — this provider is inert on
 * those routes so the step-nav / ingredient / scroll commands we shipped
 * earlier are never interfered with.
 *
 * Admin kill switch: SiteFlagsContext.voice_search_enabled gates every path.
 */
import { track } from "@/lib/analytics";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useAuth } from "@/contexts/AuthContext";
import { speak as rfSpeak, warmUpVoices, sayOnceThenBeep } from "@/lib/voice";
import api from "@/lib/api";
import { runRecipeVoice, parseLeftovers, findLeftovers } from "@/lib/voiceActions";
import { findDiets, dietListLabel } from "@/lib/diets";
import { useFavourites } from "@/contexts/FavouritesContext";
import { parseFavouritesQuery, matchFavourites, favouritePath } from "@/lib/favourites";
import { recipePath } from "@/lib/slug";

// Any phrasing that names Kitchen View / kitchen mode / cook mode ("go kitchen view", "start cooking mode").
const KITCHEN_VIEW_RE = /\bkitchen\s*(?:view|mode|screen)\b|\bcook(?:ing)?\s*(?:mode|view)\b|^(?:let'?s\s+|start\s+|begin\s+)cook(?:ing)?(?:\s+(?:this|it|now))?(?:\s+please)?[.!]?$/;
const FAV_ADD_RE = /\b(?:add|save|put|pop|stick|keep)\s+(?:this|it|that|this\s+recipe|the\s+recipe|this\s+one)?\s*(?:to|in|into|on|as|among)\s+(?:my\s+|the\s+|a\s+)?favou?rites?\b|\b(?:favou?rite|heart|like)\s+(?:this|it|that)(?:\s+recipe|\s+one)?\b|\bmark\s+(?:this|it)(?:\s+recipe)?\s+as\s+(?:a\s+)?favou?rite\b/;
const FAV_REMOVE_RE = /\b(?:remove|delete|take|drop|get)\s+(?:this|it|that|this\s+recipe|the\s+recipe|this\s+one)?\s*(?:rid\s+of\s+)?(?:from|out\s+of|off|of)\s+(?:my\s+|the\s+)?favou?rites?\b|\bun-?favou?rite\b|\bun-?heart\b/;

const PREF_ENABLED = "rf_voice_search_enabled";
const PREF_WAKE = "rf_voice_wake_word_enabled";

// Three accepted wake phrases. Transcripts are lower-cased + punctuation
// stripped before matching so punctuation / case drift from the ASR
// engine never breaks activation.
const WAKE_PHRASES = ["hey recipe filer", "hey recipefiler", "hey filer", "hey recipe"];

// Tier-1 deterministic nav table. Order matters — first match wins. Keep
// longer phrases above shorter ones so "my cookbooks" doesn't lose to
// "cookbooks" alone.
//
// `requiresAuth` → when the user isn't signed in, the dispatcher speaks
// a natural confirmation ("You need to sign in first to see your
// cookbooks") and routes them to `/login?next=<path>` instead of a
// blank 404. Everything else is unauthenticated-friendly.
const PAGE_ROUTES = [
  { patterns: [/\b(?:go\s+)?home\b/, /\btake\s+me\s+home\b/, /\bmain\s+page\b/], path: "/", label: "Home" },
  { patterns: [/\b(?:open|go\s+to|show)\s+(?:the\s+)?dashboard\b/, /\bmy\s+dashboard\b/], path: "/dashboard", label: "your dashboard", requiresAuth: true },
  // "My cookbooks" lives on the dashboard — a dedicated /cookbooks route
  // doesn't exist, so sending the user there landed them on a blank 404.
  // Fixed by pointing straight at /dashboard which lists every book.
  { patterns: [/\bmy\s+cookbooks?\b/, /\bshow\s+(?:me\s+)?(?:my\s+)?cookbooks?\b/, /\ball\s+(?:my\s+)?cookbooks?\b/], path: "/dashboard", label: "your cookbooks", requiresAuth: true },
  // Same story — recipes list is reached via the dashboard.
  { patterns: [/\bmy\s+recipes?\b/, /\bshow\s+(?:me\s+)?(?:my\s+)?recipes?\b/, /\ball\s+(?:my\s+)?recipes?\b/], path: "/dashboard", label: "your recipes", requiresAuth: true },
  { patterns: [/\bscan\s+(?:a\s+)?recipe\b/, /\badd\s+(?:a\s+)?recipe\b/, /\bnew\s+recipe\b/, /\bupload\s+(?:a\s+)?recipe\b/], path: "/scan", label: "the scanner", requiresAuth: true },
  // The route is /shopping — was pointing to the non-existent
  // /shopping-list and giving everyone a blank page.
  { patterns: [/\bshopping\s+list\b/, /\bmy\s+shopping\s+list\b/, /\bgrocery\s+list\b/], path: "/shopping", label: "your shopping list", requiresAuth: true },
  { patterns: [/\bmeal\s+plan(?:ner)?\b/, /\bplan\s+(?:my\s+)?week\b/, /\bplanner\b/], path: "/planner", label: "the meal planner", requiresAuth: true },
  { patterns: [/\bkitchen\s+help\b/, /\bglossary\b/, /\blook\s+up\s+(?:a\s+)?term\b/], path: "/kitchen-help", label: "Kitchen Help" },
  { patterns: [/\bprofile\b/, /\bmy\s+account\b/, /\bsettings?\b/], path: "/profile", label: "your profile", requiresAuth: true },
  { patterns: [/\bpricing\b/, /\bplans?\s+and\s+prices\b/, /\bsubscription\s+plans?\b/], path: "/pricing", label: "pricing" },
  { patterns: [/\bdirectory\b/, /\bbrowse\s+recipes?\b/, /\bpublic\s+recipes?\b/], path: "/directory", label: "the directory" },
  { patterns: [/\bfaqs?\b/, /\bhelp\s+(?:center|centre)\b/, /\bfrequently\s+asked\b/], path: "/faqs", label: "the FAQs" },
  { patterns: [/\bshop\b(?!\w)/, /\brecipe\s+shop\b/], path: "/shop", label: "the shop" },
  { patterns: [/\bnews\b/, /\bwhat'?s\s+new\b/, /\blatest\s+posts?\b/], path: "/news", label: "the news" },
  { patterns: [/\bleaderboard\b/, /\btop\s+cooks?\b/], path: "/leaderboard", label: "the leaderboard" },
  { patterns: [/\brefer\s+(?:a\s+)?friend\b/, /\breferral\s+program(?:me)?\b/], path: "/dashboard?refer=1", label: "the referral programme", requiresAuth: true },
  { patterns: [/\btrash\b/, /\bdeleted\s+cookbooks?\b/, /\brecycle\s+bin\b/], path: "/dashboard/trash", label: "your Trash", requiresAuth: true },
  { patterns: [/\bdiet(?:ary)?\s+(?:page|recipes?)?\b/, /\bspecial\s+diets?\b/], path: "/diet", label: "the special-diets hub" },
];

const VoiceSearchContext = createContext(null);

// Map spoken number words → zero-based list index. Covers "number
// two", "the second one", "pick 3", bare "two", bare "3", and "last".
const _PICK_WORDS = {
  "one": 0, "first": 0, "1": 0,
  "two": 1, "second": 1, "2": 1,
  "three": 2, "third": 2, "3": 2,
  "four": 3, "fourth": 3, "4": 3,
  "five": 4, "fifth": 4, "5": 4,
};
const PAGE_SIZE = 5;
const _COLL_HIST = /\b(?:historical|history|ancient|medieval|roman|romans|tudor|victorian|aztec|babylonian|mesopotamian)\b/;
const _COLL_LEAD = /^(?:please\s+)?(?:can\s+you\s+)?(?:show|find|list|get|give|search(?:\s+for)?|open|bring\s+up|pull\s+up|look\s+for)?\s*(?:me\s+)?(?:all\s+|every\s+|any\s+|some\s+)?(?:of\s+)?(?:the\s+|my\s+|our\s+)?/;

const _ADJ_RE = /\b(?:healthy|healthier|tasty|nice|good|delicious|lovely|new|different|great|simple|light|hearty)\b/g;
const _MEAL_RE = /\b(?:dinner|dinners|lunch|lunches|breakfast|breakfasts|supper|tea|brunch|meal|meals|weeknight|tonight|today|family)\b/g;
const _sq = (x) => x.replace(/\b(?:and|or|with|for|a|an|the|some|ideas?|recipes?|options?|suggestions?|dishes|please)\b/g, " ").replace(/\s+/g, " ").trim();

// "show all X recipes", "find me a healthy chicken stir fry", "give me some quick
// and easy dinner ideas" → { subject, filters } or null.
function _parseCollection(txt) {
  const t = txt.trim().replace(/[.?!]+$/, "");
  if (_COLL_HIST.test(t)) return null;
  let subject = null;
  let m = t.match(/^(.*?)\s+(?:recipes|dishes|meals|ideas|recipe\s+ideas|suggestions)(?:\s+please)?$/);
  if (m) subject = m[1].replace(_COLL_LEAD, "");
  if (!subject) {
    m = t.match(/^(?:show|find|list|get)\s+(?:me\s+)?(?:all|every)\s+(?:the\s+|my\s+)?(.+)$/)
      || t.match(/^(?:please\s+)?(?:can\s+you\s+)?(?:find|get|give|show|suggest|recommend)\s+(?:me\s+|us\s+)?(?:a|an|some|any)\s+(.+)$/)
      || t.match(/^i\s+(?:want|fancy|feel\s+like)\s+(?:a|an|some)?\s*(.+?)(?:\s+(?:tonight|today))?$/)
      || t.match(/^what\s+(?:can|should|could)\s+i\s+(?:cook|make|have)(?:\s+for)?\s+(.+)$/);
    subject = m ? m[1] : null;
  }
  subject = (subject || "").replace(/\b(?:of\s+)?(?:my|mine|our)\b/g, "").replace(/\s+/g, " ").trim();
  if (!subject || subject.length < 3 || /^(?:special\s+)?diet(?:ary)?$|^(?:public|directory|new|latest|recent|cookbooks?|ingredients?)$/.test(subject)) return null;
  const filters = {};
  if (/\b(?:quick|fast|speedy)\b/.test(subject)) filters.max = 30;
  const um = subject.match(/\b(?:under|less\s+than|within)\s+(\d+)\s*(minutes?|mins?|hours?)\b/);
  if (um) filters.max = /^h/.test(um[2]) ? parseInt(um[1], 10) * 60 : parseInt(um[1], 10);
  if (/\b(?:easy|simple)\b/.test(subject)) filters.easy = true;
  const core = _sq(subject.replace(/\b(?:quick|fast|speedy|easy)\b/g, " ").replace(/\b(?:under|less\s+than|within)\s+\d+\s*(?:minutes?|mins?|hours?)\b/g, " "));
  return { label: subject, core, filters };
}
const MORE_RE = /^(?:(?:show|give\s+me|see|hear)\s+)?(?:more|next|the\s+next(?:\s+(?:set|ones|page|five))?|next\s+(?:set|ones|page|five)|more\s+(?:options|results|please)|keep\s+going|what\s+else|any\s+more|others?)\b/;
const BACK_RE = /^(?:go\s+)?(?:back|previous|the\s+previous(?:\s+(?:set|ones|page))?|previous\s+(?:set|ones|page)|last\s+set)\b/;

function _parsePickIndex(txt) {
  const s = String(txt || "").toLowerCase();
  if (!s.trim()) return null;
  // Explicit "pick N" / "number N" / "option N" / "the Nth one" first.
  const m = s.match(/\b(?:pick|number|option|choice|go\s+to)\s+(?:number\s+)?(one|two|three|four|five|1|2|3|4|5)\b/);
  if (m) return _PICK_WORDS[m[1]] ?? null;
  const ordinal = s.match(/\b(?:the\s+)?(first|second|third|fourth|fifth)(?:\s+one)?\b/);
  if (ordinal) return _PICK_WORDS[ordinal[1]] ?? null;
  // Bare number at start / end so "two" or "3" works alone.
  const bare = s.match(/^\s*(one|two|three|four|five|1|2|3|4|5)\b\.?\s*$/);
  if (bare) return _PICK_WORDS[bare[1]] ?? null;
  return null;
}

export function VoiceSearchProvider({ children }) {
  const { flags } = useSiteFlags();
  const { user, logout } = useAuth();
  const favouritesCtx = useFavourites();
  const favouritesRef = useRef(favouritesCtx);
  favouritesRef.current = favouritesCtx;
  const nav = useNavigate();
  const location = useLocation();

  // Per-user preferences — stored in localStorage so they persist without
  // bothering the backend. Default: both OFF so we never surprise a user
  // with always-on mic.
  const [prefsEnabled, setPrefsEnabled] = useState(() => {
    try { return localStorage.getItem(PREF_ENABLED) === "1"; } catch { return false; }
  });
  const [wakeEnabled, setWakeEnabled] = useState(() => {
    try { return localStorage.getItem(PREF_WAKE) === "1"; } catch { return false; }
  });
  // Session-level pause for the wake-word scanner. When the user says
  // "stop" / "turn off voice" / "mute" we flip this to TRUE and leave
  // it true until they explicitly tap the mic FAB again — so the mic
  // doesn't stay hot in the background while they're in another app
  // or tab. Persisted to sessionStorage so a SPA nav doesn't wake it
  // back up but a fresh tab starts in the default (unpaused) state.
  const [wakePaused, setWakePausedState] = useState(() => {
    try { return sessionStorage.getItem("rf_wake_paused") === "1"; } catch { return false; }
  });
  const setWakePaused = useCallback((v) => {
    setWakePausedState(v);
    try { sessionStorage.setItem("rf_wake_paused", v ? "1" : "0"); } catch {}
  }, []);

  // Overlay UI state. `overlayMode = 'idle' | 'listening' | 'resolving' | 'confirming' | 'help'`.
  const [overlayMode, setOverlayMode] = useState("idle");
  // Bumped whenever a follow-up question is asked so the idle timer re-arms at 25s.
  const [askTick, setAskTick] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [candidates, setCandidates] = useState([]); // disambiguation list
  // Mirror of `candidates` so the stable-identity dispatchCommand
  // callback always sees the latest list without being re-created
  // (which would re-register every voice command on every pick).
  const candidatesRef = useRef([]);
  useEffect(() => { candidatesRef.current = candidates; }, [candidates]);
  // Paged disambiguation: full ranked list + current page (5 per page).
  const allMatchesRef = useRef([]);
  const [candidatePage, setCandidatePage] = useState({ start: 0, total: 0 });
  const [lastAction, setLastAction] = useState(""); // human-friendly confirmation
  const [micBlocked, setMicBlocked] = useState(false);
  // Pill chips rendered above the live transcript — pages push a short
  // confirmation label here each time a voice command succeeds, so a
  // cook glancing at the screen in a noisy kitchen can see which items
  // from a chained utterance landed on the list.
  const [recentChips, setRecentChips] = useState([]); // [{id, label, tone}]
  // Priority dispatch — when set, dispatchCommand bypasses every tier
  // and routes directly to this handler. Used by the Shopping List's
  // merge-prompt flow ("you have 1 litre of milk — add, replace, …").
  const priorityHandlerRef = useRef(null);
  // Recipe currently on screen (set by RecipeView) for "copy this recipe…" etc.
  const voiceRecipeRef = useRef(null);
  const setVoiceRecipe = useCallback((r) => { voiceRecipeRef.current = r; }, []);

  // P5 — pages register commands via useVoiceCommand(regex, handler, {label}).
  // We store them in a ref so provider doesn't re-render when a page mounts.
  const registeredRef = useRef(new Map()); // id → { pattern, handler, label }

  const adminAllowed = !!flags.voice_search_enabled;
  const onKitchenRoute = location.pathname.startsWith("/k/");
  // Overall "voice is live" — admin ON + user opted in + not in Kitchen View.
  const voiceLive = adminAllowed && prefsEnabled && !onKitchenRoute;
  // Wake-word listener is a separate always-on scan when enabled.
  const wakeLive = voiceLive && wakeEnabled;

  // ------- persistence --------
  useEffect(() => { try { localStorage.setItem(PREF_ENABLED, prefsEnabled ? "1" : "0"); } catch {} }, [prefsEnabled]);
  useEffect(() => { try { localStorage.setItem(PREF_WAKE, wakeEnabled ? "1" : "0"); } catch {} }, [wakeEnabled]);

  // ------- open overlay / stop --------
  const openOverlay = useCallback(() => {
    if (!adminAllowed) return;
    // Tapping the FAB is an explicit "I want to interact" gesture, so
    // we flip the session pause back off — otherwise saying "stop"
    // once would leave the mic muted forever until a tab reload.
    setWakePaused(false);
    // iOS Safari requires speechSynthesis.speak() to be initiated by a
    // user gesture before it will fire from any later async callback.
    // Warm the shared voice picker (and prime the engine with a silent
    // utterance) inside this click handler so the subsequent voice
    // assistant replies use the same British female voice as Kitchen
    // View and actually come out of the speaker on iPhones.
    try {
      warmUpVoices();
      rfSpeak(" ");
    } catch { /* unavailable — silent */ }
    setTranscript("");
    setCandidates([]);
    setOverlayMode("listening");
  }, [adminAllowed, setWakePaused]);
  const closeOverlay = useCallback(() => {
    setOverlayMode("idle");
    setTranscript("");
    setCandidates([]);
    setRecentChips([]);
    priorityHandlerRef.current = null;
  }, []);

  // Push a confirmation pill above the transcript. Chips auto-expire
  // after 6s so the overlay doesn't accumulate stale state across a
  // chained utterance. `tone` is "ok" | "warn" | "info".
  const pushRecentChip = useCallback((label, tone = "ok") => {
    const chip = { id: `${Date.now()}-${Math.random()}`, label, tone };
    setRecentChips((prev) => [...prev.slice(-4), chip]);
    setTimeout(() => {
      setRecentChips((prev) => prev.filter((c) => c.id !== chip.id));
    }, 6000);
  }, []);

  // Install / clear a priority dispatcher. The handler receives the
  // raw transcript and must return `true` if it consumed the utterance.
  // Returning `false` falls through to the normal command tiers.
  const setPriorityHandler = useCallback((fn) => {
    priorityHandlerRef.current = typeof fn === "function" ? fn : null;
  }, []);

  // ------- fetched approved patterns from auto-tune ------
  const [autoRoutes, setAutoRoutes] = useState([]); // [{ pattern: RegExp, path, label }]
  useEffect(() => {
    if (!voiceLive) return;
    (async () => {
      try {
        const { data } = await api.get("/voice/active-patterns");
        const compiled = (data?.patterns || [])
          .map((p) => { try { return { ...p, re: new RegExp(p.pattern, "i") }; } catch { return null; } })
          .filter(Boolean);
        setAutoRoutes(compiled);
      } catch { /* silent — the dispatcher still works with built-in patterns */ }
    })();
  }, [voiceLive]);

  // ------- command dispatcher --------
  const logPhrase = useCallback((phrase, tier, matched) => {
    // Fire-and-forget analytics ping. Failures are silent.
    try { api.post("/voice/log", { phrase, tier, matched }).catch(() => {}); } catch {}
    track("voice_search", { voice_intent: String(tier || "unknown"), voice_matched: !!matched });
  }, []);

  // Short, friendly TTS used across the dispatcher so a cook always
  // hears SOMETHING back. Routes through the shared `/app/frontend/
  // src/lib/voice.js` picker so the voice assistant speaks with the
  // SAME British English female voice used in Kitchen View — one
  // consistent "RecipeFiler voice" everywhere.
  const speak = useCallback((phrase) => {
    try { rfSpeak(phrase); } catch { /* TTS unavailable — silent */ }
  }, []);

  const candidatePageRef = useRef(0);
  const showCandidatePage = useCallback((page, query = "", fromVoice = false, lead = "") => {
    const all = allMatchesRef.current;
    const pages = Math.ceil(all.length / PAGE_SIZE);
    if (page >= pages) { speak(`That's all ${all.length} of them. Say a number, say "back" for the previous set, or say cancel.`); return; }
    if (page < 0) { speak("You're already on the first set. Say a number, or say more."); return; }
    const start = page * PAGE_SIZE;
    const slice = all.slice(start, start + PAGE_SIZE);
    candidatePageRef.current = page;
    setCandidates(slice);
    setCandidatePage({ start, total: all.length });
    const remaining = all.length - (start + slice.length);
    const moreHint = remaining > 0 ? ` There ${remaining === 1 ? "is" : "are"} ${remaining} more — say "more" to hear the next set.` : "";
    const head = page === 0 && lead ? lead : page === 0
      ? `I found ${all.length} options${query ? ` for ${query}` : ""}.${all.length > PAGE_SIZE ? ` Here are the first ${slice.length}.` : ""}`
      : `Here are options ${start + 1} to ${start + slice.length}.`;
    speak(`${head} Say the number of the one you want, or say cancel.${moreHint}`);
    if (fromVoice) setOverlayMode("confirming");
  }, [speak]);

  const dispatchCommand = useCallback(async (rawTxt) => {
    const txt = (rawTxt || "").toLowerCase().trim().replace(/[.?!,]+$/g, "");
    if (!txt) return false;
    setTranscript(txt);

    // Priority handler short-circuit — e.g. Shopping List merge prompt.
    if (priorityHandlerRef.current) {
      try {
        const consumed = await priorityHandlerRef.current(txt);
        if (consumed) {
          logPhrase(txt, "priority", true);
          return true;
        }
      } catch (e) {
        console.error("priority voice handler threw", e);
      }
    }

    // When the overlay is in disambiguation mode we have an on-screen
    // candidate list — the next utterance should be interpreted as a
    // number pick ("number two", "the first one", "second", "3"), not
    // run through the whole route/entity pipeline again. The dispatch
    // consumes the utterance even on a bad guess so the T4 miss branch
    // can't accidentally re-open a different page.
    if (candidatesRef.current.length > 0) {
      if (MORE_RE.test(txt)) {
        showCandidatePage(candidatePageRef.current + 1, "", true);
        logPhrase(txt, "pick-more", true);
        return true;
      }
      if (BACK_RE.test(txt)) {
        showCandidatePage(candidatePageRef.current - 1, "", true);
        logPhrase(txt, "pick-back", true);
        return true;
      }
      const idx = _parsePickIndex(txt);
      if (idx != null && idx < candidatesRef.current.length) {
        pickCandidate(idx);
        logPhrase(txt, "pick", true);
        return true;
      }
      // Didn't recognise the pick — stay in confirming mode, nudge.
      speak(`Say the number — one, two, three — or say cancel.`);
      logPhrase(txt, "pick-miss", false);
      return true;
    }

    // T3 global actions FIRST (so "help" / "stop" always win)
    if (/^(?:help|what can i say|voice help)\b/.test(txt)) {
      setOverlayMode("help");
      logPhrase(txt, "global", true);
      return true;
    }
    if (/^(?:stop(?:\s+listening)?|cancel|close|dismiss|never\s*mind|forget\s+it|turn\s+off\s+(?:the\s+)?voice|voice\s+off|mute(?:\s+(?:the\s+)?voice)?|be\s+quiet|shut\s+up|that'?s\s+(?:all|enough)|go\s+to\s+sleep|sleep)\b/.test(txt)) {
      // Full silence request → close the overlay AND pause the wake
      // scanner for the session. The mic will stay off until the
      // user explicitly taps the FAB (which flips wakePaused back
      // off in openOverlay). Prevents the "mic stays hot when I
      // switch apps" bug reported on iOS Safari.
      setWakePaused(true);
      setLastAction("Voice off — tap the mic to resume");
      closeOverlay();
      speak("Voice off. Tap the mic to come back.");
      logPhrase(txt, "global-stop", true);
      return true;
    }
    if (/\blog\s*out\b|\bsign\s*out\b/.test(txt)) {
      setLastAction("Signing out");
      setOverlayMode("idle");
      logPhrase(txt, "global", true);
      try { await logout?.(); } catch {}
      nav("/");
      return true;
    }
    if (/\b(?:go\s+)?back\b/.test(txt) && !/\bback\s+to\b/.test(txt)) {
      setLastAction("Going back");
      closeOverlay();
      logPhrase(txt, "global", true);
      try { window.history.back(); } catch {}
      return true;
    }

    const runPageCommand = (reg, t) => {
      setLastAction(reg.label || "Command");
      logPhrase(t, "page", true);
      try { reg.handler(t); } catch (e) { console.error("voice command handler threw", e); }
      if (reg.keepListening) {
        try { if (reg.chainPrompt !== false) rfSpeak(reg.chainPrompt || "Anything else?"); } catch { /* silent */ }
        setTranscript("");
        setOverlayMode("listening");
      } else {
        setOverlayMode("idle");
      }
    };
    // T2-priority — page commands that must beat global routing
    // (e.g. Search page "only mine", "save these as …").
    for (const [, reg] of registeredRef.current) {
      if (reg.priority && reg.pattern.test(txt)) { runPageCommand(reg, txt); return true; }
    }

    // T0-kitchen — "open / show me / go to / start Kitchen View" on a recipe page
    if (KITCHEN_VIEW_RE.test(txt.toLowerCase())) {
      const rid = voiceRecipeRef.current?.is_historical ? null : voiceRecipeRef.current?.recipe_id;
      logPhrase(txt, "kitchen-view", !!rid);
      setTranscript("");
      setOverlayMode("idle");
      if (rid) {
        setLastAction("Opening Kitchen View");
        speak("Opening Kitchen View.");
        nav(`/k/${rid}`);
      } else {
        setLastAction("Open a recipe first");
        speak("Open a recipe first, then say open Kitchen View.");
      }
      return true;
    }

    // T0-favtoggle — "add this to my favourites" / "remove this from my favourites" (recipe page only)
    const favAdd = FAV_ADD_RE.test(txt.toLowerCase());
    const favRemove = !favAdd && FAV_REMOVE_RE.test(txt.toLowerCase());
    if (favAdd || favRemove) {
      const r = voiceRecipeRef.current;
      logPhrase(txt, "favourite-toggle", !!(r && user));
      setTranscript("");
      setOverlayMode("idle");
      if (!r?.recipe_id) {
        speak(`Open the recipe you want first, then say ${favAdd ? "add this to my favourites" : "remove this from my favourites"}.`);
        return true;
      }
      if (!user) {
        speak("Please sign in first so I can save your favourites.");
        return true;
      }
      const title = r.title || "this recipe";
      const favourites = favouritesRef.current;
      const kind = r.is_historical ? "historical" : "recipe";
      const saved = favourites?.isSaved(kind, r.recipe_id);
      if (favAdd === !!saved) {
        speak(favAdd ? `${title} is already in your favourites.` : `${title} isn't in your favourites.`);
        return true;
      }
      setLastAction(favAdd ? "Saving to Favourites…" : "Removing from Favourites…");
      const ok = await favourites.toggle(kind, r.recipe_id, (msg) => speak(`Sorry, I couldn't do that. ${msg}`));
      if (ok) {
        setLastAction(favAdd ? `Saved ${title} to Favourites` : `Removed ${title} from Favourites`);
        speak(favAdd ? `Done. ${title} has been added to your favourites.` : `Done. ${title} has been removed from your favourites.`);
      }
      return true;
    }

    // T0a — recipe actions: copy to cookbook, ingredients → shopping list, meal plan
    {
      const ask = (fn) => {
        const h = (r) => {
          priorityHandlerRef.current = null;
          const res = fn(r);
          if (res === "retry") { priorityHandlerRef.current = h; setAskTick((t) => t + 1); return true; }
          return !!res;
        };
        priorityHandlerRef.current = h;
        setAskTick((t) => t + 1);
      };
      const handled = runRecipeVoice(txt, {
        recipe: voiceRecipeRef.current, user, speak, ask,
        onDone: () => setLastAction("Done"),
      });
      if (handled) {
        setLastAction("Working on it…");
        setTranscript("");
        setOverlayMode("listening");
        logPhrase(txt, "recipe-action", true);
        return true;
      }
    }

    const profile = user?.diet_profile || [];
    const keep = profile.length ? " Keeping to your diet." : "";
    // T0-leftovers — "what can I make with chicken and rice?"
    const left = parseLeftovers(txt);
    if (left) {
      setOverlayMode("resolving");
      try {
        const data = await findLeftovers(left, profile);
        const matches = data?.matches || [];
        const ings = (data?.ingredients || left).join(" and ").replace(/ and (?=.* and )/g, ", ");
        logPhrase(txt, "leftovers", matches.length > 0);
        if (!matches.length) {
          setLastAction(`No recipes found using ${ings}`);
          setOverlayMode("listening");
          sayOnceThenBeep(`I couldn't find any recipes using ${ings}${keep ? " that fit your diet" : ""}. Try naming a different ingredient.`, speak);
          return false;
        }
        const n = data.ingredients.length;
        const top = matches.slice(0, 3).map((m, i) => `${i + 1}, ${m.label}${n > 1 ? (m.matched === n ? `, uses ${n === 2 ? "both" : "all of them"}` : `, uses ${m.uses.join(" and ")}`) : ""}`).join(". ");
        allMatchesRef.current = matches;
        setLastAction(`Recipes using ${ings}`);
        showCandidatePage(0, "", true, `I found ${matches.length} recipe${matches.length === 1 ? "" : "s"} using ${ings}.${keep} Top ones: ${top}.`);
        setOverlayMode("confirming");
        return true;
      } catch {
        setOverlayMode("listening");
        speak("Sorry, I couldn't look that up just now.");
        return false;
      }
    }

    // T0-fav — "show me my favourites", "open my favourite apple pie recipe"
    const favQ = parseFavouritesQuery(txt);
    if (favQ) {
      logPhrase(txt, "favourites", true);
      if (!user) {
        setOverlayMode("idle");
        speak("You need to sign in first to see your favourites. I'll take you to the sign-in page.");
        nav("/login?next=%2Ffavourites");
        return true;
      }
      try {
        const { data } = await api.get("/favourites");
        const live = (data?.items || []).filter((it) => it.available);
        setOverlayMode("idle");
        if (!favQ.term) {
          setLastAction("Opening your Favourites");
          speak(live.length ? `Opening your favourites. You've saved ${live.length} recipe${live.length === 1 ? "" : "s"}.` : "Your favourites are empty. Tap the heart on any recipe to save it.");
          nav("/favourites");
          return true;
        }
        const hits = matchFavourites(live, favQ.term);
        if (hits.length === 1) {
          setLastAction(`Opening ${hits[0].title}`);
          speak(`Opening your favourite ${hits[0].recipe?.title || hits[0].title}.`);
          nav(favouritePath(hits[0], recipePath));
        } else if (hits.length > 1) {
          setLastAction(`Favourites matching ${favQ.term}`);
          speak(`You have ${hits.length} favourites matching ${favQ.term}: ${hits.slice(0, 5).map((h, i) => `${i + 1}, ${h.title}`).join(". ")}.`);
          nav(`/favourites?q=${encodeURIComponent(favQ.term)}`);
        } else {
          setLastAction(`No favourite ${favQ.term}`);
          speak(`I couldn't find ${favQ.term} in your favourites, so here's the full list.`);
          nav("/favourites");
        }
        return true;
      } catch {
        setOverlayMode("listening");
        speak("Sorry, I couldn't open your favourites just now.");
        return false;
      }
    }

    // T0-diet — "show me nut-free dinners", "vegan gluten-free curries"
    const dq = /^(?:what|whats|what's|define|meaning|is\s+(?:this|it))\b/.test(txt) ? { diets: [] } : findDiets(txt);
    if (dq.diets.length) {
      const spoken = dq.diets.length;
      dq.diets = [...new Set([...dq.diets, ...profile])];
      const term = _sq(dq.rest.replace(/\b(?:show|find|give|get|search|look|me|us|some|any|all|of|my|please|i\s+want|i'?d\s+like|i\s+fancy|food|that\s+are|which\s+are|only|just|can\s+you|could\s+you)\b/g, " "));
      const label = dietListLabel(dq.diets);
      const diet = dq.diets.join(",");
      const hasAll = (it) => dq.diets.every((d) => (it.diet_badges || []).includes(d));
      setOverlayMode("resolving");
      try {
        let list = [];
        if (term.length >= 2) {
          const { data } = await api.get("/search", { params: { q: term, limit: 60 } });
          list = [...(data?.groups?.recipes || []), ...(data?.groups?.historical || [])].filter(hasAll);
        }
        let fallback = false;
        if (!list.length) {
          const { data } = await api.get("/search", { params: { q: label, limit: 60, browse: true, diet } });
          list = (data?.groups?.recipes || []).filter(hasAll);
          fallback = true;
        }
        logPhrase(txt, "diet", list.length > 0);
        if (!list.length) {
          setOverlayMode("listening");
          setLastAction(`No ${label} recipes yet`);
          sayOnceThenBeep(`I couldn't find any ${label} recipes yet.`, speak);
          return false;
        }
        const top = list.slice(0, 5).map((it, i) => `${i + 1}, ${it.title || "untitled recipe"}`).join(". ");
        const n = `${list.length} ${label} recipe${list.length === 1 ? "" : "s"}`;
        const head = fallback && term.length >= 2
          ? `I couldn't find any ${label} ${term}, so here are all ${n}.`
          : `I found ${fallback ? n : `${list.length} ${label} ${list.length === 1 ? term.replace(/(?:es|s)$/, "") : term}`}.`;
        setLastAction(fallback ? `All ${label} recipes` : `${label} ${term}`);
        setOverlayMode("idle");
        speak(`${head}${dq.diets.length > spoken ? keep : ""} Top ones: ${top}. Say a number to open one.`);
        const qs = new URLSearchParams({ q: fallback ? label : term, diet });
        if (fallback) qs.set("browse", "1");
        nav(`/search?${qs.toString()}`);
        return true;
      } catch {
        setOverlayMode("listening");
      }
    }

    // T0 — collection intent: "show me all the vegan recipes", "find me a
    // healthy chicken stir fry", "quick and easy dinner ideas" → all-sources search.
    const coll = _parseCollection(txt);
    if (coll) {
      setLastAction(`Searching ${coll.label}…`);
      try {
        const f = coll.filters;
        const pass = (it) => (!f.max || (it.total_minutes && it.total_minutes <= f.max)) && (!f.easy || it.difficulty === "Easy")
          && profile.every((d) => (it.diet_badges || []).includes(d));
        const listOf = (d) => [...(d?.groups?.recipes || []), ...(d?.groups?.historical || [])].filter(pass);
        const noAdj = _sq(coll.core.replace(_ADJ_RE, " "));
        const noMeal = _sq(noAdj.replace(_MEAL_RE, " "));
        const longest = noMeal.split(" ").sort((a, b) => b.length - a.length)[0] || "";
        const terms = [...new Set([coll.core, noAdj, noMeal, longest])].filter((x) => x.length >= 3);
        let hit = null;
        for (const term of terms) {
          const { data } = await api.get("/search", { params: { q: term, limit: 60 } });
          const list = listOf(data);
          if (list.length) { hit = { term, list, browse: false }; break; }
        }
        if (!hit && (f.max || f.easy || !noMeal)) {
          const { data } = await api.get("/search", { params: { q: coll.label, limit: 60, browse: true, diet: profile.join(",") } });
          const list = listOf(data);
          if (list.length) hit = { term: coll.label, list, browse: true };
        }
        if (!hit) {
          setOverlayMode("listening");
          setLastAction(`Nothing found for ${coll.label}`);
          sayOnceThenBeep(`I couldn't find anything for ${coll.label}. Try another word.`, speak);
          logPhrase(txt, "collection", false);
          return false;
        }
        const desc = [f.max ? `under ${f.max} minutes` : "", f.easy ? "easy" : ""].filter(Boolean).join(" and ");
        const what = hit.browse ? `${desc || ""} ideas`.trim() : `${hit.term} recipes${desc ? `, ${desc}` : ""}`;
        const top = hit.list.slice(0, 5).map((it, i) => `${i + 1}, ${it.title}`).join(". ");
        setLastAction(`Showing ${what}`);
        setOverlayMode("idle");
        speak(`I found ${hit.list.length} ${what}.${keep} Top ones: ${top}. Say a number to open one, or say more filters like "only mine".`);
        logPhrase(txt, "collection", true);
        const qs = new URLSearchParams({ q: hit.term });
        if (hit.browse) qs.set("browse", "1");
        if (f.max) qs.set("max", String(f.max));
        if (f.easy) qs.set("easy", "1");
        nav(`/search?${qs.toString()}`);
        return true;
      } catch { /* fall through to normal routing */ }
    }

    // T0-faq — spoken help questions ("how do I share my shopping list?") → short FAQ answer,
    // then offer the full FAQ page. Runs before page routes so questions don't just navigate.
    if (/^(?:how|can|what|why|where|when|does|do|is|are|will|should|could)\b/.test(txt) && !/^(?:can|could|will|would)\s+you\b/.test(txt)
        && !/^(?:where|is\s+there|are\s+there|what\s+time|how\s+do\s+i\s+(?:get|go)\s+to)\b/.test(txt)) {
      try {
        const { data } = await api.get("/voice/faq-match", { params: { q: txt } });
        const m = data?.match;
        if (m) {
          setLastAction(m.question);
          setTranscript("");
          setOverlayMode("listening");
          logPhrase(txt, "faq", true);
          speak(`${m.short} Want to see the full answer?`);
          priorityHandlerRef.current = (reply) => {
            priorityHandlerRef.current = null;
            if (/\b(?:yes|yeah|yep|sure|please|ok(?:ay)?|show|open|go on)\b/.test(reply)) {
              speak("Opening the answer.");
              setOverlayMode("idle");
              nav(`/faqs/${m.slug}`);
            } else {
              speak("Okay.");
            }
            return true;
          };
          setAskTick((t) => t + 1);
          return true;
        }
      } catch { /* fall through to normal routing */ }
    }

    // T1 deterministic page routes
    for (const row of PAGE_ROUTES) {
      if (row.patterns.some((p) => p.test(txt))) {
        // Auth gate — if the user asked for something personal (My
        // cookbooks, Shopping list, Profile…) but isn't signed in,
        // route them to login with a next-param and SPEAK a natural
        // explanation. Prevents the "blank page" bug where they
        // landed on a 404 after a wake-word nav.
        if (row.requiresAuth && !user) {
          const label = row.label || "that page";
          setLastAction(`Sign in required for ${label}`);
          setOverlayMode("idle");
          speak(`You need to sign in first to see ${label}. I'll take you to the sign-in page.`);
          logPhrase(txt, "route-auth-gate", true);
          const next = encodeURIComponent(row.path);
          nav(`/login?next=${next}`);
          return true;
        }
        const label = row.label || row.path;
        setLastAction(`Opening ${label}`);
        setOverlayMode("idle");
        speak(`Opening ${label}`);
        logPhrase(txt, "route", true);
        nav(row.path);
        return true;
      }
    }

    // T1b — admin-approved auto-tune patterns (merged live from backend)
    for (const row of autoRoutes) {
      if (row.re.test(txt)) {
        setLastAction(row.label || `Opening ${row.path}`);
        setOverlayMode("idle");
        logPhrase(txt, "route", true);
        if (row.path) nav(row.path);
        return true;
      }
    }

    // T2 page-registered commands (P5)
    for (const [, reg] of registeredRef.current) {
      if (reg.pattern.test(txt)) {
        runPageCommand(reg, txt);
        return true;
      }
    }

    // T4 fuzzy resolver (recipes / cookbooks) — strip verbs, keep the entity
    setOverlayMode("resolving");
    const intent = /\bcook\b/.test(txt) ? "cook" : /\bprint\b/.test(txt) ? "print" : "open";
    // Scope detection: let the user bias the search explicitly.
    //   "from the directory" / "a public recipe" / "find me a public"
    //     → scope="public" — rank Directory matches ABOVE owned.
    //   "from my cookbooks" / "in my cookbooks" / "my own" / "mine"
    //     → scope="owned"  — only search the caller's own content.
    //   Otherwise → "all" — current owned-first behaviour.
    let scope = "all";
    if (/\b(?:from|in|on)\s+(?:the\s+)?(?:public\s+)?directory\b/.test(txt)
        || /\ba?\s*(?:public|directory|published)\s+recipe\b/.test(txt)
        || /\bpublic\s+(?:cook)?book\b/.test(txt)) {
      scope = "public";
    } else if (/\b(?:from|in|on)\s+my\s+(?:own\s+)?(?:cookbooks?|recipes?|books?|collection)\b/.test(txt)
        || /\bmy\s+own\s+(?:cookbooks?|recipes?)\b/.test(txt)
        || /\bone\s+of\s+mine\b/.test(txt)) {
      scope = "owned";
    }
    // Strip the leading verb(s) + fillers + any scope phrase so the
    // backend scoring sees the actual title.
    let query = txt
      .replace(/^(?:please\s+)?(?:can\s+you\s+)?(?:now\s+)?/, "")
      .replace(/^(?:cook|print|open|show|find|take me to|go to|navigate to|load|bring up|pull up)\s+/, "")
      .replace(/^(?:me\s+)?(?:the\s+|my\s+|a\s+)?/, "")
      .replace(/\b(?:recipe|cookbook)\b/g, "")
      .replace(/\b(?:called|named)\b/g, "")
      .replace(/\bfrom\s+(?:the\s+)?(?:public\s+)?directory\b/, "")
      .replace(/\b(?:from|in|on)\s+my\s+(?:own\s+)?(?:cookbooks?|recipes?|books?|collection)\b/, "")
      .replace(/\bmy\s+own\b/, "")
      .replace(/\b(?:public|directory|published)\b/g, "")
      .replace(/\s+/g, " ")
      .trim();
    // Drop a stray "book" only when something meaningful is left — keeps titles like "B Book" / "Be Book".
    // A lone short word ("bee", "sea", "b") is probably a letter-named title like "B Book", so keep "book".
    const withoutBook = query.replace(/\bbook\b/g, "").replace(/\s+/g, " ").trim();
    if (withoutBook.length >= 5 || withoutBook.includes(" ")) query = withoutBook;
    // Owned-scope needs auth — the backend would silently return empty.
    if (scope === "owned" && !user) {
      setLastAction("Sign in to search your own cookbooks");
      setOverlayMode("idle");
      speak("You need to sign in first to search your own cookbooks. I'll take you to the sign-in page.");
      logPhrase(txt, "scope-owned-auth-gate", true);
      nav("/login?next=%2Fdashboard");
      return true;
    }
    try {
      const { data } = await api.post("/voice/resolve", { query, intent, scope });
      const matches = data?.matches || [];
      if (matches.length === 1 || (matches.length > 1 && matches[0].score >= 0.9)) {
        const top = matches[0];
        const verb = top.action === "cook" ? "Cooking" : top.action === "print" ? "Printing" : "Opening";
        setLastAction(`${verb} ${top.label}`);
        setOverlayMode("idle");
        // "what is arrowroot?" → read the Kitchen Help definition straight away.
        speak(top.kind === "kitchen_help" && top.is_question && top.definition
          ? `${top.label}: ${top.definition}`
          : `${verb} ${top.label}`);
        logPhrase(txt, "entity", true);
        nav(top.path);
        return true;
      }
      if (matches.length > 1) {
        allMatchesRef.current = matches;
        showCandidatePage(0, query);
        setOverlayMode("confirming");
        logPhrase(txt, "entity", true);
        return true;
      }
      if (data?.kh_missing) {
        setLastAction(`"${data.kh_term}" isn't in Kitchen Help yet — we've noted it`);
        setOverlayMode("listening");
        sayOnceThenBeep(`Sorry, ${data.kh_term} isn't in Kitchen Help yet. I've let the team know so they can add it.`, speak);
        logPhrase(txt, "kh-missing", false);
        return false;
      }
      // No matches → stay in listening mode and ask conversationally.
      // Short list of helpful examples so the cook isn't guessing what
      // phrases work. Also updates the on-screen `lastAction` so a
      // mic-muted viewer gets the same hint visually.
      setLastAction(`Sorry — I didn't catch that. Try "my cookbooks" or "shopping list".`);
      setOverlayMode("listening");
      sayOnceThenBeep(`Sorry, I'm not sure what you'd like. You can say "find" followed by a recipe or ingredient, "what can I make with" some ingredients, "my cookbooks", "my shopping list", "my meal plan", "scan a recipe", or "cook" followed by a recipe name.`, speak);
      logPhrase(txt, "miss", false);
      return false;
    } catch (e) {
      console.error("voice resolve failed", e);
      setOverlayMode("listening");
      speak(`Sorry, something went wrong — try again?`);
      logPhrase(txt, "miss", false);
      return false;
    }
  }, [nav, user, logout, closeOverlay, logPhrase, autoRoutes, speak, showCandidatePage]);
  // QA hook: lets automated tests drive the voice pipeline with text.
  useEffect(() => { window.__rfVoiceDispatch = dispatchCommand; }, [dispatchCommand]);

  // Pick disambiguation option: "number 2" / "the first one" / "second"
  const pickCandidate = useCallback((idxOrTxt) => {
    const list = candidatesRef.current;
    if (!list.length) return;
    let idx = -1;
    if (typeof idxOrTxt === "number") idx = idxOrTxt;
    else {
      const s = String(idxOrTxt).toLowerCase();
      const map = { "one": 0, "first": 0, "1": 0, "two": 1, "second": 1, "2": 1,
                     "three": 2, "third": 2, "3": 2, "four": 3, "fourth": 3, "4": 3,
                     "five": 4, "fifth": 4, "5": 4 };
      for (const k of Object.keys(map)) if (new RegExp(`\\b${k}\\b`).test(s)) { idx = map[k]; break; }
    }
    if (idx < 0 || idx >= list.length) return;
    const pick = list[idx];
    allMatchesRef.current = [];
    if (pick.kind === "kitchen_help" && pick.is_question && pick.definition) speak(`${pick.label}: ${pick.definition}`);
    setLastAction(`Opening ${pick.label}`);
    setOverlayMode("idle");
    setCandidates([]);
    candidatesRef.current = [];
    nav(pick.path);
  }, [nav, speak]);

  // ------- voice recognition — WAKE WORD session (always-on scan) -------
  const [wakeReady] = useState(true);
  const wakeHandler = useCallback((txt) => {
    const t = (txt || "").toLowerCase().replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
    if (!t) return false;
    if (WAKE_PHRASES.some((p) => t.includes(p))) {
      openOverlay();
      return true;
    }
    return false;
  }, [openOverlay]);
  useVoiceRecognition({
    enabled: wakeLive && overlayMode === "idle" && !wakePaused,
    ready: wakeReady,
    onCommand: wakeHandler,
    onBlocked: () => setMicBlocked(true),
  });

  // ------- voice recognition — COMMAND session (open overlay) -----------
  const commandHandler = useCallback((txt) => {
    // In confirming mode, interpret as a pick
    if (overlayMode === "confirming") {
      const t = (txt || "").toLowerCase();
      if (/\bcancel\b|\bnever\s*mind\b|\bforget\s+it\b/.test(t)) { closeOverlay(); return true; }
      pickCandidate(t);
      return true;
    }
    // Otherwise normal dispatch
    dispatchCommand(txt);
    return true;
  }, [overlayMode, dispatchCommand, pickCandidate, closeOverlay]);
  useVoiceRecognition({
    enabled: voiceLive && (overlayMode === "listening" || overlayMode === "resolving" || overlayMode === "confirming"),
    ready: true,
    onCommand: commandHandler,
    onInterim: (t) => setTranscript(t || ""),
    onBlocked: () => { setMicBlocked(true); closeOverlay(); },
    // Dispatch only on FINAL transcripts so a multi-word query ("cake
    // recipes") isn't acted on halfway through ("cake" interim).
    matchInterim: false,
  });

  // 10-second idle auto-close for the overlay. When the mic is open
  // but no transcript has arrived (user walked away, phone in pocket,
  // tab backgrounded) we close the overlay AND pause the wake scanner
  // so the mic doesn't stay hot across tabs or apps. Timer resets
  // every time an interim arrives so a slow speaker never gets cut
  // off mid-sentence. In `confirming` mode we give the user a bit
  // longer (14s) since they're reading the on-screen candidates.
  const idleTimerRef = useRef(null);
  useEffect(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (overlayMode === "idle" || overlayMode === "help") return undefined;
    // A pending follow-up question (e.g. "save these dinners?") gets 25s,
    // and we never time out while the assistant is still talking.
    const ms = priorityHandlerRef.current ? 25000 : overlayMode === "confirming" ? 14000 : 10000;
    const fire = () => {
      if (typeof window !== "undefined" && window.speechSynthesis?.speaking) {
        idleTimerRef.current = setTimeout(fire, priorityHandlerRef.current ? 25000 : ms);
        return;
      }
      // Only auto-stop when there's genuinely nothing happening — if
      // the user is mid-utterance (transcript is non-empty) we don't
      // yank the mic. The hook calls onInterim as soon as speech is
      // detected, so `transcript` being set means we're actively
      // mid-command.
      setLastAction("No activity — voice off");
      setWakePaused(true);
      setOverlayMode("idle");
      setTranscript("");
      setCandidates([]);
      setRecentChips([]);
      priorityHandlerRef.current = null;
      try { rfSpeak("Still there? I'll stop listening. Tap the mic to come back."); } catch {}
    };
    idleTimerRef.current = setTimeout(fire, ms);
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [overlayMode, transcript, askTick, setWakePaused]);

  // Background-tab guard. On iOS Safari, SpeechRecognition keeps the
  // mic indicator alive even after the user swipes to another app.
  // Flip wakePaused when the page becomes hidden so the recogniser
  // tears down on its next cycle. We leave the user's PREF untouched
  // — tapping the FAB when they return clears the pause.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        setWakePaused(true);
        setOverlayMode("idle");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [setWakePaused]);

  // ------- useVoiceCommand hook (P5) — expose register/unregister -------
  const register = useCallback((id, pattern, handler, label, opts = {}) => {
    registeredRef.current.set(id, {
      pattern, handler, label,
      keepListening: !!opts.keepListening,
      chainPrompt: opts.chainPrompt ?? null,
      priority: !!opts.priority,
    });
  }, []);
  const unregister = useCallback((id) => {
    registeredRef.current.delete(id);
  }, []);

  const value = useMemo(() => ({
    // state
    enabled: voiceLive,
    adminAllowed,
    prefsEnabled,
    wakeEnabled,
    overlayMode,
    transcript,
    candidates,
    candidatePage,
    setVoiceRecipe,
    showMoreCandidates: () => showCandidatePage(candidatePageRef.current + 1),
    showPrevCandidates: () => showCandidatePage(candidatePageRef.current - 1),
    lastAction,
    micBlocked,
    onKitchenRoute,
    recentChips,
    // True when the mic is passively listening for a wake phrase — used
    // by the FAB to render a subtle green pulse so the cook can tell at
    // a glance "the site is listening". False when overlay is already
    // open, wake-word disabled in Profile, admin killswitch off, mic
    // was blocked by the browser, OR the session is paused (user said
    // "stop", tab backgrounded, or 10-second idle timeout fired).
    wakeArmed: wakeLive && overlayMode === "idle" && !micBlocked && !wakePaused,
    // Wake word is on but the session is paused — FAB shows a red dot.
    micPaused: wakeLive && overlayMode === "idle" && !micBlocked && wakePaused,
    // actions
    openOverlay,
    closeOverlay,
    dispatchCommand,
    pickCandidate,
    setPrefsEnabled,
    setWakeEnabled,
    register,
    unregister,
    pushRecentChip,
    setPriorityHandler,
  }), [setVoiceRecipe, voiceLive, adminAllowed, prefsEnabled, wakeEnabled, wakePaused, overlayMode, transcript, candidates, candidatePage, showCandidatePage, lastAction, micBlocked, onKitchenRoute, recentChips, openOverlay, closeOverlay, dispatchCommand, pickCandidate, register, unregister, pushRecentChip, setPriorityHandler]);

  return <VoiceSearchContext.Provider value={value}>{children}</VoiceSearchContext.Provider>;
}

export function useVoiceSearch() {
  const ctx = useContext(VoiceSearchContext);
  if (!ctx) throw new Error("useVoiceSearch must be used inside <VoiceSearchProvider>");
  return ctx;
}

/**
 * useVoiceCommand(id, pattern, handler, label, opts?) — pages register a regex
 * command the global voice overlay will fire while on that page. The id
 * must be stable (unique per command). Auto-unregisters on unmount.
 *
 * `opts.keepListening = true` → after the handler fires, the overlay stays
 * in listening mode, TTS speaks `opts.chainPrompt` (default "Anything else?"),
 * so the cook can chain multiple commands hands-free.
 */
export function useVoiceCommand(id, pattern, handler, label, opts) {
  const { register, unregister } = useVoiceSearch();
  // Stabilise opts across renders so we don't re-register on every tick.
  const optsSig = JSON.stringify(opts || {});
  useEffect(() => {
    register(id, pattern, handler, label, opts);
    return () => unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pattern, handler, label, optsSig, register, unregister]);
}
